/**
 * ChatPanel wrapper for workspace context
 *
 * Delegates to @papan/agent/ui ChatPanel with the current workspace ID,
 * and resolves the workspace's linked folders into display-ready scope
 * rows for the agent panel's welcome screen (the agent package doesn't
 * depend on workspace config, so the resolution lives here: alias wins,
 * else the target folder's name via the shared file-detail cache).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChatPanel as AgentChatPanel, type ScopeLink } from '@papan/agent/ui'
import { fetchAndCacheFileInfo, fileKeys } from '@papan/files/client/queries'
import { useRealtime } from '@papan/realtime/client'
import type { EditorSelection } from '@papan/collab/client'
import type { WorkspaceLink } from '@papan/workspace/types'

/** Active tab info passed from workspace */
export interface ActiveTabInfo {
  fileId: string
  fileName: string
  filePath: string
  fileType: string
}

interface ChatPanelProps {
  workspaceId?: string
  workspaceFolderId?: string | null
  onOpenFile?: (path: string, at?: { line?: number }) => void
  /** Open Settings → Billing from the agent panel's error banner. Passed
   *  straight through; this wrapper adds no behaviour of its own. */
  onOpenBilling?: () => void
  activeTab?: ActiveTabInfo | null
  editorSelectionRef?: RefObject<EditorSelection | null>
  contextMutedRef?: RefObject<boolean>
  onMuteContext?: () => void
  orgEmail?: string
  /** Mobile layout — see AgentChatPanel: overlay owns the title, export +
   *  budget portal into `mobileHeaderSlot`, display-options drops to the
   *  session-tabs row. */
  mobile?: boolean
  mobileHeaderSlot?: HTMLElement | null
  /** Workspace links (from useWorkspace) — resolved to ScopeLink rows for
   *  the welcome screen's visibility card. */
  links?: WorkspaceLink[]
  /** Container kind — passed straight through so the welcome card's files row
   *  matches the explorer's section header. */
  entityKind?: 'project' | 'miniapp'
  /** Whether the caller can write to this workspace — passed straight
   *  through to gate composer attachment uploads (#639, Task 3.4). Forward
   *  only; this wrapper adds no default of its own (AgentChatPanel defaults
   *  to `true` when omitted). */
  canWrite?: boolean
  /** Settings → Storage, for the chat-storage banner (#758). */
  onManageChatStorage?: () => void
}

export function ChatPanel({ workspaceId, workspaceFolderId, onOpenFile, onOpenBilling, activeTab, editorSelectionRef, contextMutedRef, onMuteContext, orgEmail, mobile, mobileHeaderSlot, links, entityKind, canWrite, onManageChatStorage }: ChatPanelProps) {
  const queryClient = useQueryClient()
  // Resolved rows, each tagged with the folder it names — a rename event finds its row by id.
  const [rows, setRows] = useState<{ folderId: string | null; link: ScopeLink }[] | undefined>(undefined)
  const scopeLinks = useMemo(() => rows?.map(r => r.link), [rows])
  // Links whose name follows the folder (no alias, not missing).
  const followsRef = useRef<Set<string>>(new Set())
  // #939: a rename that arrived while a request for that folder's info was in flight — the
  // request was asked BEFORE the rename, so its answer carries the old name. Re-applied the
  // moment it lands (below), so neither the cache nor a resolve sharing it keeps the old name.
  const repatchRef = useRef<Map<string, string>>(new Map())

  // The cached file info of a renamed folder carries its new name (a later resolve reads it).
  const patchCachedName = useCallback((folderId: string, name: string) => {
    queryClient.setQueryData<{ name?: string }>(fileKeys.detail(folderId), old => (old ? { ...old, name } : old))
  }, [queryClient])
  const showName = useCallback((folderId: string, name: string) => {
    setRows(prev => prev?.map(r => (r.folderId === folderId && r.link.name !== name ? { ...r, link: { ...r.link, name } } : r)))
  }, [])

  useEffect(() => queryClient.getQueryCache().subscribe(event => {
    if (event.type !== 'updated' || repatchRef.current.size === 0) return
    const { action } = event
    // Only a FETCH finishing matters: a write to the cache (ours, or another reader's
    // setQueryData) is a 'success' too, but `manual` — it is not the older answer landing.
    const settled = (action.type === 'success' && !action.manual) || action.type === 'error'
    if (!settled) return
    for (const [folderId, name] of repatchRef.current) {
      if (JSON.stringify(event.query.queryKey) !== JSON.stringify(fileKeys.detail(folderId))) continue
      repatchRef.current.delete(folderId)
      if (action.type === 'error') continue // nothing landed to correct; keep no stale entry
      patchCachedName(folderId, name)
      showName(folderId, name)
    }
  }), [queryClient, patchCachedName, showName])

  useEffect(() => {
    if (!links) { setRows(undefined); return }
    let alive = true
    const ordered = [...links].sort((a, b) => a.sortOrder - b.sortOrder)
    followsRef.current = new Set(ordered.filter(l => !l.alias && !l.missing).map(l => l.targetFolderId))
    Promise.all(ordered.map(async (l): Promise<{ folderId: string | null; link: ScopeLink }> => {
      if (l.missing) return { folderId: null, link: { name: l.alias || l.path || 'Linked folder', permission: l.permission, missing: true } }
      if (l.alias) return { folderId: null, link: { name: l.alias, permission: l.permission } }
      try {
        const info = await fetchAndCacheFileInfo(queryClient, l.targetFolderId)
        return { folderId: l.targetFolderId, link: { name: info.name || 'Linked folder', permission: l.permission } }
      } catch {
        return { folderId: l.targetFolderId, link: { name: 'Linked folder', permission: l.permission } }
      }
    })).then(resolved => {
      if (!alive) return
      // Read every name back from the cache NOW, when the card is set — not when each folder
      // answered: a rename that landed in between (or re-applied over an older answer) is
      // there, while the answers collected here may predate it.
      setRows(resolved.map(r => {
        const cached = r.folderId ? queryClient.getQueryData<{ name?: string }>(fileKeys.detail(r.folderId))?.name : undefined
        return cached && cached !== r.link.name ? { ...r, link: { ...r.link, name: cached } } : r
      }))
    })
    return () => { alive = false }
  }, [links, queryClient])

  // #939: a linked folder renamed elsewhere — show its new name on the welcome card. The event
  // carries the new name; a link with an alias keeps the alias, a missing link isn't resolved.
  const { onFileEvent } = useRealtime()
  useEffect(() => onFileEvent((event) => {
    if (event.type !== 'file:moved' && event.type !== 'file:updated') return
    const name = event.name
    const folderId = event.fileId
    if (!name || !followsRef.current.has(folderId)) return
    if (queryClient.getQueryState(fileKeys.detail(folderId))?.fetchStatus === 'fetching') repatchRef.current.set(folderId, name)
    else repatchRef.current.delete(folderId)
    patchCachedName(folderId, name)
    showName(folderId, name)
  }), [onFileEvent, queryClient, patchCachedName, showName])

  if (!workspaceId) {
    return (
      <div className="h-full ws-card flex items-center justify-center">
        <p className="text-sm themed-text-muted">No workspace selected</p>
      </div>
    )
  }

  return <AgentChatPanel workspaceId={workspaceId} workspaceFolderId={workspaceFolderId} onOpenFile={onOpenFile} onOpenBilling={onOpenBilling} activeTab={activeTab} editorSelectionRef={editorSelectionRef} contextMutedRef={contextMutedRef} onMuteContext={onMuteContext} orgEmail={orgEmail} mobile={mobile} mobileHeaderSlot={mobileHeaderSlot} scopeLinks={scopeLinks} entityKind={entityKind} canWrite={canWrite} onManageChatStorage={onManageChatStorage} />
}
